import type { makeFetchTransport } from '@sentry/react';
import { hasSecretInLocation, redactAccessKeysDeep } from './access-key-redaction';

type Transport = ReturnType<typeof makeFetchTransport>;

// Once a secret has been visited, recorders stay off until a full document load.
// Shared with deferred SDK startup so navigating away cannot restart recording.
let secretVisited = false;

export function preventRecordingForLocation(pathname: string, search: string): boolean {
    secretVisited ||= hasSecretInLocation(pathname, search);
    return secretVisited;
}

export function isSessionRecordingBlocked(): boolean {
    if (typeof window !== 'undefined') {
        preventRecordingForLocation(window.location.pathname, window.location.search);
    }
    return secretVisited;
}

/** Replay bypasses beforeSend. Drop the entire recording, including compressed data,
 * when its metadata contains a secret, or this document has visited a secret URL.
 * Redacting only replay_event.urls would leave credentials inside replay_recording.
 */
export function protectReplayTransport(transport: Transport): Transport {
    return {
        ...transport,
        send(envelope) {
            const replayItems = envelope[1].filter(
                ([header]) => header.type === 'replay_event' || header.type === 'replay_recording',
            );
            const unsafeMetadata = replayItems.some(
                ([header, payload]) => header.type === 'replay_event' && redactAccessKeysDeep(payload) !== payload,
            );
            if (unsafeMetadata) secretVisited = true;
            if (replayItems.length > 0 && isSessionRecordingBlocked()) {
                return Promise.resolve({});
            }
            return transport.send(envelope);
        },
    };
}
